import { useState } from "react";
import { Calendar, ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { CALENDLY_URL } from "@/lib/siteData";

/**
 * Inline Calendly embed - styled to match the site theme.
 * Variants:
 *  - inline (default)  - renders the full iframe in place
 *  - modal             - renders a button that opens the iframe in a modal
 *  - button            - renders a button that opens Calendly in a new tab
 */
const CALENDLY_PARAMS = "?hide_event_type_details=1&hide_gdpr_banner=1&background_color=0d1015&text_color=ede5d3&primary_color=d6a04a";

export const CalendlyButton = ({ label = "Book a 15-min call", className = "", testId = "calendly-button", icon = true }) => (
  <Button asChild data-testid={testId} className={`h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-6 text-sm font-medium ${className}`}>
    <a href={CALENDLY_URL} target="_blank" rel="noreferrer noopener">
      {icon && <Calendar className="mr-2 h-4 w-4" />}{label}
    </a>
  </Button>
);

export const CalendlyModalButton = ({ label = "Book a 15-min call", className = "", testId = "calendly-modal-button" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button data-testid={testId} onClick={() => setOpen(true)} className={`h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-6 text-sm font-medium ${className}`}>
        <Calendar className="mr-2 h-4 w-4" />{label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl w-[94vw] h-[80vh] p-0 bg-[hsl(225,22%,4%)] border-border overflow-hidden">
          <DialogTitle className="sr-only">Book a call</DialogTitle>
          <button onClick={() => setOpen(false)} className="absolute top-3 right-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-card/80 hover:bg-card border border-border" aria-label="Close"><X className="h-4 w-4" /></button>
          <iframe data-testid="calendly-modal-iframe" src={`${CALENDLY_URL}${CALENDLY_PARAMS}`} title="Book a call" className="h-full w-full bg-black" />
        </DialogContent>
      </Dialog>
    </>
  );
};

export const CalendlyInline = ({ kicker = "Or book directly", title = "Pick a slot - 15 minutes, no pressure.", subtitle = "Prefer a quick call? Grab a slot below and we'll talk through your project together.", height = "640px", testId = "calendly-inline" }) => {
  return (
    <section data-testid={testId} className="section-pad bg-[hsl(225,22%,4%)] border-y border-border">
      <div className="container-site grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-4">
          <p className="kicker mb-3">{kicker}</p>
          <h2 className="font-display uppercase text-foreground text-3xl sm:text-4xl tracking-[0.02em] leading-[1.02] text-balance">{title}</h2>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed max-w-md">{subtitle}</p>
          <ul className="mt-6 space-y-2 text-sm text-foreground/85">
            <li className="flex items-start gap-2"><span className="mt-2 inline-block h-1 w-1 rounded-full bg-[hsl(var(--primary))]" />Free 15-min discovery call</li>
            <li className="flex items-start gap-2"><span className="mt-2 inline-block h-1 w-1 rounded-full bg-[hsl(var(--primary))]" />Walk through your brief, dates and budget</li>
            <li className="flex items-start gap-2"><span className="mt-2 inline-block h-1 w-1 rounded-full bg-[hsl(var(--primary))]" />Get a treatment direction + clear estimate</li>
          </ul>
          <div className="mt-6"><CalendlyButton testId="calendly-inline-newtab" label="Open in new tab" icon={false} className="h-10 px-4 bg-card border border-border text-foreground hover:bg-[hsl(225,18%,9%)]" /></div>
        </div>
        <div className="lg:col-span-8 rounded-2xl border border-border overflow-hidden bg-black">
          <iframe data-testid={`${testId}-iframe`} src={`${CALENDLY_URL}${CALENDLY_PARAMS}`} title="Book a strategy call" className="w-full bg-black" style={{ height }} />
        </div>
      </div>
    </section>
  );
};

export default CalendlyInline;
